import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import { Shell } from "./layout/Shell";
import { Login } from "./pages/Login";
import { SetPassword } from "./pages/SetPassword";
import { Home } from "./pages/Home";

import { LeadsList } from "./pages/crm/LeadsList";
import { LeadDetail } from "./pages/crm/LeadDetail";
import { AccountsList } from "./pages/crm/AccountsList";
import { AccountProfile } from "./pages/crm/AccountProfile";
import { ActivitiesList } from "./pages/crm/ActivitiesList";
import { CalendarPage } from "./pages/crm/Calendar";

import { DealsList } from "./pages/sales/DealsList";
import { Pipeline } from "./pages/sales/Pipeline";
import { LostAnalysis } from "./pages/sales/LostAnalysis";
import { QuotationsList } from "./pages/sales/QuotationsList";
import { QuotationDetail } from "./pages/sales/QuotationDetail";
import { ContractsList } from "./pages/sales/ContractsList";
import { ServicesList } from "./pages/sales/ServicesList";
import { PackagesList } from "./pages/sales/PackagesList";
import {
  CommissionRunDetail,
  CommissionRuns,
  MyCommissions,
} from "./pages/sales/Commissions";
import { SalesRep } from "./pages/sales/SalesRep";
import { SalesTeam } from "./pages/sales/SalesTeam";
import { Targets } from "./pages/sales/Targets";
import { ContractDetail } from "./pages/sales/ContractDetail";
import { WarRoom } from "./pages/crm/WarRoom";
import { BookingPages } from "./pages/sales/BookingPages";
import { Chat } from "./pages/chat/Chat";
import { Channels } from "./pages/crm/Channels";
import { CallConsole } from "./pages/callcenter/Console";
import { CallsHistory } from "./pages/callcenter/History";
import { CallCenterDashboard } from "./pages/callcenter/Dashboard";
import { CallQuality } from "./pages/callcenter/Quality";
import { CallCenterSettings } from "./pages/callcenter/Settings";
import { MonitorScreen } from "./pages/callcenter/Monitor";
import { MonitorSettings } from "./pages/callcenter/MonitorSettings";
import { DriveIntegration } from "./pages/settings/DriveIntegration";
import { DesktopApp } from "./pages/settings/DesktopApp";
import { MetaChannels } from "./pages/settings/MetaChannels";
import { ContactsList } from "./pages/crm/ContactsList";
import { Inbox } from "./pages/crm/Inbox";
import { MeetingsList } from "./pages/crm/MeetingsList";
import { MeetingRoom } from "./pages/crm/MeetingRoom";
import { SuppliersList } from "./pages/finance/SuppliersList";
import { BillsList } from "./pages/finance/BillsList";
import { ExpensesList } from "./pages/finance/ExpensesList";
import { CashAccountsList } from "./pages/finance/CashAccountsList";
import { RecurringList } from "./pages/finance/RecurringList";
import { RecurringEditor } from "./pages/finance/RecurringEditor";
import { RecurringDetail } from "./pages/finance/RecurringDetail";
import { RecurringBillsList } from "./pages/finance/RecurringBillsList";
import { RecurringBillEditor } from "./pages/finance/RecurringBillEditor";
import { RecurringBillDetail } from "./pages/finance/RecurringBillDetail";
import { AssetsList } from "./pages/assets/AssetsList";
import { AssetDetail } from "./pages/assets/AssetDetail";
import { AssetCategories } from "./pages/assets/AssetCategories";
import { AssetCounts, AssetCountSheet } from "./pages/assets/AssetCounts";
import {
  DepreciationRuns,
  DepreciationRunDetail,
} from "./pages/assets/Depreciation";
import { Balances } from "./pages/finance/Balances";
import { PartyStatement } from "./pages/finance/PartyStatement";
import { ReportBuilder } from "./pages/reports/ReportBuilder";
import { MailSettings } from "./pages/settings/MailSettings";
import { Profile } from "./pages/settings/Profile";
import { SubscriptionPage } from "./pages/account/Subscription";
import { CreditRules } from "./pages/settings/CreditRules";
import { ScreenPermission } from "./pages/settings/ScreenPermission";

import { InvoicesList } from "./pages/finance/InvoicesList";
import { InvoiceDetail } from "./pages/finance/InvoiceDetail";
import { PaymentsList } from "./pages/finance/PaymentsList";
import { AgingReport } from "./pages/finance/Aging";
import { CreditExposure } from "./pages/finance/CreditExposure";
import { ChartOfAccounts } from "./pages/accounting/ChartOfAccounts";
import { JournalList } from "./pages/accounting/JournalList";
import { FinancialReports } from "./pages/accounting/FinancialReports";
import { PrintDocument } from "./pages/print/PrintDocument";
import { PrintContract } from "./pages/print/PrintContract";
import { PrintStatement } from "./pages/print/PrintStatement";
import { PrintCommissionSlip } from "./pages/print/PrintCommissionSlip";
import { PrintCommissionRun } from "./pages/print/PrintCommissionRun";
import { PrintOffer } from "./pages/print/PrintOffer";
import { PrintReport } from "./pages/print/PrintReport";
import { PrintTable } from "./pages/print/PrintTable";
import { ModuleDashboard } from "./pages/ModuleDashboard";
import { Landing } from "./pages/public/Landing";
import { FindCompany } from "./pages/public/FindCompany";
import { ForgotPassword } from "./pages/ForgotPassword";
import { isLocalHost, isRootSite, tenantUrl } from "./lib/site";
import { Signup } from "./pages/public/Signup";
import { AcceptInvite } from "./pages/public/AcceptInvite";
import { PublicBooking, BookingPreview } from "./pages/public/PublicBooking";
import { PublicRate } from "./pages/public/PublicRate";
import { Users } from "./pages/settings/Users";
import { Roles } from "./pages/settings/Roles";
import { Lookups } from "./pages/settings/Lookups";
import { RatingSettings } from "./pages/settings/RatingSettings";
import { SecuritySettings } from "./pages/settings/Security";
import { SavedMessages } from "./pages/settings/SavedMessages";
import { CustomFieldsPage } from "./pages/settings/CustomFieldsPage";
import { AuditLog } from "./pages/settings/AuditLog";
import { DataMove } from "./pages/settings/DataMove";
import {
  NotificationsPage,
  NotificationSettings,
} from "./pages/settings/NotificationsPage";
import { Meetings } from "./pages/settings/Meetings";
import { Automation } from "./pages/settings/Automation";
import { WorkSites } from "./pages/settings/WorkSites";
import { RequestTypes } from "./pages/settings/RequestTypes";
import { PayrollRules } from "./pages/settings/PayrollRules";
import { HrHome } from "./pages/hr/HrHome";
import { OrgChart } from "./pages/hr/OrgChart";
import { OrgUnits } from "./pages/hr/OrgUnits";
import { OrgStructure } from "./pages/hr/OrgStructure";
import { Company } from "./pages/hr/Company";
import { Benefits } from "./pages/hr/Benefits";
import { Documents } from "./pages/hr/Documents";
import { Performance } from "./pages/hr/Performance";
import { PerformanceCycle } from "./pages/hr/PerformanceCycle";
import { Learning } from "./pages/hr/Learning";
import { TrainingSession } from "./pages/hr/TrainingSession";
import { Relations } from "./pages/hr/Relations";
import { CaseDetail } from "./pages/hr/CaseDetail";
import { PulseSurveyPage } from "./pages/hr/PulseSurvey";
import { Talent } from "./pages/hr/Talent";
import { TalentReview } from "./pages/hr/TalentReview";
import { CampaignDetail } from "./pages/hr/CampaignDetail";
import { Applications } from "./pages/hr/Applications";
import { Contracts } from "./pages/hr/Contracts";
import { Positions, PositionDetail } from "./pages/hr/Positions";
import { Grades } from "./pages/hr/Grades";
import { Succession } from "./pages/hr/Succession";
import { WorkforcePlanning } from "./pages/hr/Planning";
import { HrAnalytics } from "./pages/hr/HrAnalytics";
import { EmployeesList, PendingEmployees } from "./pages/hr/EmployeesList";
import { EmployeeProfile } from "./pages/hr/EmployeeProfile";
import { AttendanceSheet } from "./pages/hr/AttendanceSheet";
import { LeavesList } from "./pages/hr/LeavesList";
import { HrRequests } from "./pages/hr/Requests";
import { PayrollRuns, PayrollRunDetail } from "./pages/hr/Payroll";
import {
  RecruitOverview,
  Campaigns,
  ApplicationForms,
} from "./pages/hr/Recruiting";
import { Jobs, JobDetail } from "./pages/hr/Jobs";
import { RecruitPipeline, ApplicationDetail } from "./pages/hr/Pipeline";
import { RecruitAgenda } from "./pages/hr/Agenda";
import { PublicJob } from "./pages/public/PublicJob";
import { PublicCareers } from "./pages/public/PublicCareers";
import { PublicOffer } from "./pages/public/PublicOffer";
import { CompanySettings } from "./pages/settings/CompanySettings";
import { CommissionSettings } from "./pages/settings/CommissionSettings";
import { WarRoomRules } from "./pages/settings/WarRoomRules";
import { AgencyHome } from "./pages/agency/Overview";
import { AgencyOnboarding } from "./pages/agency/Onboarding";
import { AgencyProjects } from "./pages/agency/Projects";
import { AgencyProjectBoard } from "./pages/agency/ProjectBoard";
import { MyAgencyTasks } from "./pages/agency/MyTasks";
import { AgencyRetainers } from "./pages/agency/Retainers";
import { AgencyPortfolios } from "./pages/agency/Portfolios";
import { AgencyPortfolioDetail } from "./pages/agency/PortfolioDetail";
import { AgencyGoals } from "./pages/agency/Goals";
import { AgencyTemplates } from "./pages/agency/Templates";
import { AgencyTeam } from "./pages/agency/Team";
import { AgencyTeamMember } from "./pages/agency/TeamMember";
import {
  MyProfile,
  MyLeaves,
  MyAttendance,
  MyPayslips,
  MyCustody,
} from "./pages/my/MyHr";
import { MyRequests } from "./pages/my/MyRequests";

/** تحويلٌ يحفظ الاستعلام: تبويبُ الصفحة القديمة (`?tab=`) يصل إلى الجديدة. */
function KeepSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

export function App() {
  const { session } = useAuth();
  const location = useLocation();

  /*
   * صفحة الحجز قبل كل شيء.
   *
   * زائرٌ لا حساب له يفتح رابطًا أُرسل إليه. وانتظارُه بوابةَ استعادة
   * الجلسة يعني هيكلًا فارغًا يومض قبل صفحةٍ لا تخصّ الجلسة أصلًا — وقد
   * يُحوَّل إلى شاشة الدخول لو تأخّرت.
   */
  if (location.pathname.startsWith("/book/")) {
    return (
      <Routes>
        {/* معاينة المحرّر — الشاشة نفسها ببيانات لم تُحفظ بعد، داخل إطار. */}
        <Route path="/book/preview" element={<BookingPreview />} />
        <Route path="/book/:tenant/:slug" element={<PublicBooking />} />
      </Routes>
    );
  }

  /*
   * صفحةُ التقديم وصفحةُ وظائف الشركة — عامّتان كالحجز وللسبب نفسه.
   *
   * والمساران تحت البادئة نفسِها: رابطٌ واحدٌ يُحفظ ويُنشر، وما تحته
   * وظيفةٌ بعينها.
   */
  if (location.pathname.startsWith("/apply/")) {
    return (
      <Routes>
        <Route path="/apply/:tenant" element={<PublicCareers />} />
        <Route path="/apply/:tenant/:slug" element={<PublicJob />} />
      </Routes>
    );
  }

  /* عرضُ العمل — رمزٌ في الرابط يعرّف صاحبَه، بلا حسابٍ ولا كلمة سرّ. */
  if (location.pathname.startsWith("/offer/")) {
    return (
      <Routes>
        <Route path="/offer/:token" element={<PublicOffer />} />
      </Routes>
    );
  }

  /* صفحة تقييم المكالمة — عامةٌ كالحجز وللسبب نفسه. */
  if (location.pathname.startsWith("/rate/")) {
    return (
      <Routes>
        <Route path="/rate/:token" element={<PublicRate />} />
      </Routes>
    );
  }

  /*
   * الموقع العامّ (goyone.com): تعريفٌ وتسجيلٌ فقط — حتى لمن له جلسة.
   *
   * الدخول على نطاق الشركة وحده، فلا تُعرض لوحةٌ هنا ولا نموذجُ دخول.
   * «الدخول» يسأل عن الشركة ويحوّل إلى نطاقها، وأيُّ رابطٍ داخليٍّ يُحال إليه.
   */
  if (isRootSite()) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/login" element={<FindCompany />} />
        <Route path="*" element={<ToTenantSite />} />
      </Routes>
    );
  }

  /*
   * ثلاث حالات لا اثنتان.
   *
   * undefined تعني أن استعادة الجلسة لم تنتهِ بعد. عرض شاشة الدخول حينها
   * يُظهرها للحظة لمن هو مسجَّل الدخول فعلًا، وهو وميض مزعج يوحي بأن الجلسة
   * ضاعت.
   */
  if (session === undefined) {
    return (
      <div className="login">
        <div
          className="col"
          style={{ alignItems: "center", gap: "var(--sp-3)" }}
        >
          <div className="skeleton" style={{ width: 180, height: 12 }} />
          <div className="skeleton" style={{ width: 120, height: 12 }} />
        </div>
      </div>
    );
  }

  /*
   * بلا جلسة على نطاق الشركة: الدخول هو الباب.
   *
   * الصفحة التعريفية والتسجيل على الموقع العامّ؛ نطاقُ الشركة لموظفيها.
   * وأيّ صفحةٍ داخلية تُطلب تحوّل إلى الدخول ثم تعود إليها بعده.
   * (على localhost يبقى الموقع كلّه في نطاقٍ واحد للتطوير.)
   */
  if (session === null) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/invite" element={<AcceptInvite />} />
        {isLocalHost() && <Route path="/signup" element={<Signup />} />}
        {isLocalHost() && <Route path="/" element={<Landing />} />}
        <Route path="*" element={<ToLogin />} />
      </Routes>
    );
  }

  /*
   * دخلَ بكلمةٍ مؤقّتة: لا شاشةَ قبل أن يضبط كلمتَه.
   * المؤقّتةُ معروفةٌ لغيره (بريدٌ أو ورقة)، فلا يُفتح النظامُ عليها.
   */
  if (session.user.mustChangePassword) {
    return <SetPassword />;
  }

  return (
    <Routes>
      {/*
        مسار الطباعة خارج الهيكل عمدًا: المستند يشغل الصفحة وحده بلا شريط
        علوي ولا قائمة جانبية، فيطابق ما سيخرج من الطابعة.
      */}
      <Route path="/invite" element={<AcceptInvite />} />

      <Route path="/print/contract/:id" element={<PrintContract />} />
      <Route path="/print/offer/:id" element={<PrintOffer />} />
      <Route path="/print/report" element={<PrintReport />} />
      <Route path="/print/table" element={<PrintTable />} />
      <Route
        path="/print/statement/:accountId/:sid"
        element={<PrintStatement />}
      />
      <Route
        path="/print/commission-slip/:runId/:employeeId"
        element={<PrintCommissionSlip />}
      />
      <Route
        path="/print/commission-run/:id"
        element={<PrintCommissionRun />}
      />
      <Route path="/print/:kind/:id" element={<PrintDocument />} />

      <Route element={<Shell />}>
        <Route path="/" element={<Home />} />
        <Route path="/my-work" element={<ActivitiesList mineOnly />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/chat/:id" element={<Chat />} />

        {/* إدارة العملاء: المتابعة والتواصل */}
        <Route path="/crm" element={<Navigate to="/crm/leads" replace />} />
        <Route path="/crm/leads" element={<LeadsList />} />
        <Route path="/crm/leads/:id" element={<LeadDetail />} />
        <Route path="/crm/accounts" element={<AccountsList />} />
        <Route path="/crm/accounts/:id" element={<AccountProfile />} />
        <Route path="/crm/contacts" element={<ContactsList />} />
        <Route path="/crm/activities" element={<ActivitiesList />} />
        {/* صفحاتُ الحجز انتقلت إلى المبيعات — والقديمُ يُحوَّل فلا ينكسر رابطٌ محفوظ. */}
        <Route
          path="/crm/booking"
          element={<Navigate to="/sales/booking" replace />}
        />
        <Route
          path="/crm/calls"
          element={<ActivitiesList fixedType="CALL" />}
        />
        <Route path="/crm/inbox" element={<Inbox />} />
        <Route path="/crm/channels" element={<Channels />} />
        <Route path="/crm/meetings" element={<MeetingsList />} />
        <Route path="/crm/meetings/:id/room" element={<MeetingRoom />} />
        <Route path="/callcenter" element={<CallConsole />} />
        <Route path="/callcenter/history" element={<CallsHistory />} />
        <Route path="/callcenter/dashboard" element={<CallCenterDashboard />} />
        <Route path="/callcenter/quality" element={<CallQuality />} />
        <Route path="/monitor" element={<MonitorScreen />} />
        <Route path="/settings/monitoring" element={<MonitorSettings />} />
        <Route
          path="/monitor/settings"
          element={<Navigate to="/settings/monitoring" replace />}
        />
        <Route path="/settings/callcenter" element={<CallCenterSettings />} />
        <Route path="/settings/drive" element={<DriveIntegration />} />
        <Route path="/settings/desktop-app" element={<DesktopApp />} />
        <Route path="/crm/calendar" element={<CalendarPage />} />
        <Route path="/crm/war-room" element={<WarRoom />} />

        {/* المبيعات: الصفقات والمستندات */}
        <Route path="/sales" element={<Navigate to="/sales/deals" replace />} />
        <Route path="/sales/deals" element={<DealsList />} />
        <Route path="/sales/pipeline" element={<Pipeline />} />
        <Route path="/sales/lost" element={<LostAnalysis />} />
        <Route path="/sales/booking" element={<BookingPages />} />
        <Route path="/sales/quotations" element={<QuotationsList />} />
        <Route path="/sales/quotations/:id" element={<QuotationDetail />} />
        <Route path="/sales/contracts" element={<ContractsList />} />
        <Route path="/sales/contracts/:id" element={<ContractDetail />} />
        <Route path="/sales/team" element={<SalesTeam />} />
        <Route path="/sales/team/:id" element={<SalesRep />} />
        <Route path="/settings/sales-targets" element={<Targets />} />
        <Route
          path="/sales/targets"
          element={<Navigate to="/settings/sales-targets" replace />}
        />
        <Route path="/sales/commissions" element={<CommissionRuns />} />
        <Route
          path="/sales/commissions/:id"
          element={<CommissionRunDetail />}
        />
        <Route path="/my/commissions" element={<MyCommissions />} />
        <Route path="/my/profile" element={<MyProfile />} />
        <Route path="/my/leaves" element={<MyLeaves />} />
        <Route path="/my/requests" element={<MyRequests />} />
        <Route path="/my/attendance" element={<MyAttendance />} />
        <Route path="/my/payslips" element={<MyPayslips />} />
        <Route path="/my/custody" element={<MyCustody />} />
        <Route path="/sales/services" element={<ServicesList />} />
        <Route path="/sales/packages" element={<PackagesList />} />

        {/* إدارة الوكالة: مراحل دورة العميل — لوحة، انضمام، مشاريع، مهام، باقات */}
        <Route path="/agency" element={<AgencyHome />} />
        <Route path="/agency/onboarding" element={<AgencyOnboarding />} />
        <Route path="/agency/projects" element={<AgencyProjects />} />
        <Route path="/agency/projects/:id" element={<AgencyProjectBoard />} />
        <Route path="/agency/my" element={<MyAgencyTasks />} />
        <Route path="/agency/retainers" element={<AgencyRetainers />} />
        <Route path="/agency/portfolios" element={<AgencyPortfolios />} />
        <Route path="/agency/portfolios/:id" element={<AgencyPortfolioDetail />} />
        <Route path="/agency/goals" element={<AgencyGoals />} />
        <Route
          path="/settings/project-templates"
          element={<AgencyTemplates />}
        />
        <Route
          path="/agency/templates"
          element={<Navigate to="/settings/project-templates" replace />}
        />
        <Route path="/agency/team" element={<AgencyTeam />} />
        <Route path="/agency/team/:id" element={<AgencyTeamMember />} />

        {/* الحسابات */}
        <Route
          path="/finance"
          element={<Navigate to="/finance/invoices" replace />}
        />
        <Route path="/finance/invoices" element={<InvoicesList />} />
        <Route path="/finance/invoices/:id" element={<InvoiceDetail />} />
        <Route path="/finance/payments" element={<PaymentsList />} />
        <Route
          path="/finance/receivables"
          element={<Balances key="receivables" side="receivables" />}
        />
        <Route
          path="/finance/payables"
          element={<Balances key="payables" side="payables" />}
        />
        <Route path="/finance/statements" element={<PartyStatement key="customer" side="customer" />} />
        <Route path="/finance/supplier-statements" element={<PartyStatement key="supplier" side="supplier" />} />
        <Route path="/finance/suppliers" element={<SuppliersList />} />
        <Route path="/finance/bills" element={<BillsList />} />
        <Route path="/finance/recurring-bills" element={<RecurringBillsList />} />
        <Route path="/finance/recurring-bills/new" element={<RecurringBillEditor />} />
        <Route path="/finance/recurring-bills/:id" element={<RecurringBillDetail />} />
        <Route
          path="/finance/recurring-bills/:id/edit"
          element={<RecurringBillEditor />}
        />
        <Route path="/finance/expenses" element={<ExpensesList />} />
        <Route path="/finance/recurring" element={<RecurringList />} />
        <Route path="/finance/recurring/new" element={<RecurringEditor />} />
        <Route path="/finance/recurring/:id" element={<RecurringDetail />} />
        <Route
          path="/finance/recurring/:id/edit"
          element={<RecurringEditor />}
        />
        <Route path="/finance/banks" element={<CashAccountsList />} />

        {/* الأصول الثابتة — الثابت قبل :id حتى لا يلتقطه المعرّف */}
        <Route path="/assets" element={<AssetsList />} />
        <Route
          path="/settings/asset-categories"
          element={<AssetCategories />}
        />
        <Route
          path="/assets/categories"
          element={<Navigate to="/settings/asset-categories" replace />}
        />
        <Route path="/assets/counts" element={<AssetCounts />} />
        <Route path="/assets/counts/:id" element={<AssetCountSheet />} />
        <Route path="/assets/depreciation" element={<DepreciationRuns />} />
        <Route
          path="/assets/depreciation/:id"
          element={<DepreciationRunDetail />}
        />
        <Route path="/assets/:id" element={<AssetDetail />} />
        <Route path="/finance/accounts" element={<ChartOfAccounts />} />
        <Route path="/finance/journal" element={<JournalList />} />
        <Route path="/finance/aging" element={<AgingReport />} />
        <Route path="/finance/credit" element={<CreditExposure />} />
        <Route path="/finance/reports" element={<FinancialReports />} />

        {/* التقارير */}
        <Route
          path="/reports"
          element={<Navigate to="/reports/sales" replace />}
        />
        {/* لوحاتُ الوحدات — تقاريرُ كلّ وحدةٍ ومنشوراتُ الفريق عليها. */}
        <Route
          path="/crm/dashboard"
          element={<ModuleDashboard module="crm" />}
        />
        <Route
          path="/sales/dashboard"
          element={<ModuleDashboard module="sales" />}
        />
        <Route
          path="/finance/dashboard"
          element={<ModuleDashboard module="finance" />}
        />
        <Route path="/hr/dashboard" element={<ModuleDashboard module="hr" />} />
        <Route
          path="/agency/dashboard"
          element={<ModuleDashboard module="agency" />}
        />
        <Route
          path="/callcenter/insights"
          element={<ModuleDashboard module="callcenter" />}
        />
        {/* اللوحتان القديمتان صارتا لوحةَ المبيعات — والروابطُ المحفوظة تُحوَّل. */}
        <Route
          path="/reports/sales"
          element={<Navigate to="/sales/dashboard" replace />}
        />
        <Route
          path="/reports/team"
          element={<Navigate to="/sales/dashboard" replace />}
        />
        <Route path="/reports/builder" element={<ReportBuilder />} />

        {/* الإعداداتُ تفتح على تفضيلات الشخص ثمّ إعدادات الشركة. */}
        <Route
          path="/settings"
          element={<Navigate to="/settings/notifications" replace />}
        />
        {/* حسابي — الملفُّ والاشتراكُ فقط، مستقلٌّ عن إعدادات الشركة. */}
        <Route path="/account" element={<Profile />} />
        <Route path="/account/subscription" element={<SubscriptionPage />} />
        {/* التفضيلاتُ الشخصية تحت الإعدادات — والقديمُ يُحوَّل. */}
        <Route
          path="/account/notifications"
          element={<Navigate to="/settings/notifications" replace />}
        />
        <Route
          path="/account/screen"
          element={<Navigate to="/settings/screen-monitoring" replace />}
        />
        {/* المساراتُ القديمة تحت الإعدادات تُحوَّل إلى «حسابي». */}
        <Route
          path="/settings/profile"
          element={<Navigate to="/account" replace />}
        />
        <Route
          path="/settings/account"
          element={<Navigate to="/account" replace />}
        />
        <Route path="/settings/company" element={<CompanySettings />} />
        <Route path="/settings/branding" element={<CompanySettings />} />
        <Route
          path="/settings/finance-defaults"
          element={<CompanySettings />}
        />
        <Route path="/settings/credit-rules" element={<CreditRules />} />
        <Route
          path="/settings/screen-monitoring"
          element={<ScreenPermission />}
        />
        <Route path="/settings/users" element={<Users />} />
        <Route path="/settings/roles" element={<Roles />} />

        {/* الموارد البشرية */}
        <Route path="/hr" element={<HrHome />} />
        <Route path="/hr/org" element={<OrgChart />} />
        {/* إعدادُ المؤسسة صار في الإعدادات — والقديمُ يُحوَّل. */}
        <Route path="/settings/hr-company" element={<Company />} />
        <Route path="/settings/org-structure" element={<OrgStructure />} />
        <Route path="/settings/org-units" element={<OrgUnits />} />
        <Route
          path="/hr/setup/company"
          element={<KeepSearch to="/settings/hr-company" />}
        />
        <Route
          path="/hr/setup/structure"
          element={<Navigate to="/settings/org-structure" replace />}
        />
        <Route
          path="/hr/setup/units"
          element={<Navigate to="/settings/org-units" replace />}
        />
        <Route path="/hr/org/positions" element={<Positions />} />
        <Route path="/hr/org/positions/:id" element={<PositionDetail />} />
        <Route path="/settings/grades" element={<Grades />} />
        <Route
          path="/hr/org/grades"
          element={<Navigate to="/settings/grades" replace />}
        />
        <Route path="/hr/org/succession" element={<Succession />} />
        <Route path="/hr/org/planning" element={<WorkforcePlanning />} />
        <Route path="/hr/analytics" element={<HrAnalytics />} />
        <Route path="/hr/employees" element={<EmployeesList />} />
        <Route path="/hr/employees/pending" element={<PendingEmployees />} />
        <Route path="/hr/contracts" element={<Contracts />} />
        <Route path="/hr/documents" element={<Documents />} />
        <Route path="/hr/benefits" element={<Benefits />} />
        <Route path="/hr/performance" element={<Performance />} />
        <Route path="/hr/learning" element={<Learning />} />
        <Route path="/hr/relations" element={<Relations />} />
        <Route path="/hr/talent" element={<Talent />} />
        <Route path="/hr/talent/reviews/:id" element={<TalentReview />} />
        <Route path="/hr/relations/cases/:id" element={<CaseDetail />} />
        <Route path="/hr/relations/surveys/:id" element={<PulseSurveyPage />} />
        <Route path="/hr/learning/sessions/:id" element={<TrainingSession />} />
        <Route
          path="/hr/performance/cycles/:id"
          element={<PerformanceCycle />}
        />
        <Route path="/hr/employees/:id" element={<EmployeeProfile />} />
        <Route path="/hr/attendance" element={<AttendanceSheet />} />
        <Route path="/settings/worksites" element={<WorkSites />} />
        <Route path="/settings/request-types" element={<RequestTypes />} />
        <Route path="/settings/payroll-rules" element={<PayrollRules />} />
        <Route path="/hr/leaves" element={<LeavesList />} />
        <Route path="/hr/requests" element={<HrRequests />} />
        <Route path="/hr/payroll" element={<PayrollRuns />} />
        <Route path="/hr/payroll/:id" element={<PayrollRunDetail />} />
        <Route path="/hr/recruit" element={<RecruitOverview />} />
        <Route path="/hr/recruit/jobs" element={<Jobs />} />
        <Route path="/hr/recruit/jobs/:id" element={<JobDetail />} />
        <Route path="/hr/recruit/applications" element={<Applications />} />
        <Route path="/hr/recruit/pipeline" element={<RecruitPipeline />} />
        <Route path="/hr/recruit/agenda" element={<RecruitAgenda />} />
        <Route
          path="/hr/recruit/applications/:id"
          element={<ApplicationDetail />}
        />
        <Route path="/hr/recruit/campaigns" element={<Campaigns />} />
        <Route path="/hr/recruit/campaigns/:id" element={<CampaignDetail />} />
        <Route
          path="/settings/application-forms"
          element={<ApplicationForms />}
        />
        <Route
          path="/hr/recruit/forms"
          element={<Navigate to="/settings/application-forms" replace />}
        />
        <Route path="/settings/mail" element={<MailSettings />} />
        <Route path="/settings/meta" element={<MetaChannels />} />
        <Route path="/settings/meetings" element={<Meetings />} />
        <Route path="/settings/commissions" element={<CommissionSettings />} />
        <Route path="/settings/war-room-rules" element={<WarRoomRules />} />
        <Route path="/settings/lookups" element={<Lookups />} />
        <Route path="/settings/ratings" element={<RatingSettings />} />
        <Route path="/settings/security" element={<SecuritySettings />} />
        <Route path="/settings/messages" element={<SavedMessages />} />
        <Route path="/settings/custom-fields" element={<CustomFieldsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route
          path="/settings/notifications"
          element={<NotificationSettings />}
        />
        <Route path="/settings/audit" element={<AuditLog />} />
        <Route path="/settings/data" element={<DataMove />} />
        <Route path="/settings/automation" element={<Automation />} />

        {/* المسارات القديمة قبل نقل الصفقات إلى المبيعات */}
        <Route
          path="/crm/deals"
          element={<Navigate to="/sales/deals" replace />}
        />
        <Route
          path="/crm/pipeline"
          element={<Navigate to="/sales/pipeline" replace />}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
      {/* بعد الدخول: يعود إلى الصفحة التي طلبها قبله (?next=). */}
      <Route path="/login" element={<AfterLogin />} />
    </Routes>
  );
}

/**
 * مسارٌ داخليٌّ آمن من ?next= — يبدأ بـ «/» ولا يخرج إلى نطاقٍ آخر.
 *
 * «//evil.com» و«/\evil.com» يقرؤهما المتصفّح رابطًا خارجيًّا، فيصير
 * التحويل بعد الدخول بابًا للتصيّد.
 */
function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) {
    return "/";
  }
  return raw.startsWith("/login") ? "/" : raw;
}

function AfterLogin() {
  const { search } = useLocation();
  return <Navigate to={safeNext(new URLSearchParams(search).get("next"))} replace />;
}

/**
 * الزائر بلا جلسة يطلب صفحةً داخلية: إلى الدخول، ومعه طريقُ العودة.
 *
 * بدونه يفتح الموظفُ رابطَ صفقةٍ أُرسل إليه فيجد نفسه على الرئيسية بعد
 * الدخول، ويبحث عنها من جديد.
 */
function ToLogin() {
  const { pathname, search } = useLocation();
  const next = pathname === "/" ? "" : `?next=${encodeURIComponent(pathname + search)}`;
  return <Navigate to={`/login${next}`} replace />;
}

/**
 * الموقع العامّ يُحيل ما يخصّ شركةً إلى نطاقها.
 *
 * رابطُ دعوةٍ قديم على goyone.com يحمل الشركة في ?tenant= — فيُكمَل على
 * نطاقها بالمسار والاستعلام نفسيهما. وما لا يُعرف له شركة يذهب إلى «ادخل إلى
 * شركتك» ومعه المسار ليُكمَل بعد اختيارها.
 */
function ToTenantSite() {
  const { pathname, search } = useLocation();
  const tenant = new URLSearchParams(search).get("tenant");
  if (tenant) {
    window.location.replace(tenantUrl(tenant, pathname + search));
    return null;
  }
  const next = pathname === "/" ? "" : `?next=${encodeURIComponent(pathname + search)}`;
  return <Navigate to={`/login${next}`} replace />;
}
